// Kundenprofil → Eigene Felder: zeigt die mandantenweit definierten Felder
// (nur aktive, im Profil sichtbare) und speichert die Kundenwerte.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, ListChecks } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomerCustomFieldsTab = ({ customerId }) => {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [obResponses, setObResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [defsRes, valRes, obRes] = await Promise.all([
        axios.get(`${API}/settings/custom-fields`),
        axios.get(`${API}/customers/${customerId}/custom-fields`),
        axios.get(`${API}/customers/${customerId}/onboarding-responses`),
      ]);
      const defs = (defsRes.data?.fields || []).filter((f) => f.enabled && f.visibility?.profile);
      setFields(defs);
      setValues(valRes.data?.values || {});
      setObResponses(obRes.data?.responses || []);
    } catch (e) {
      toast.error('Eigene Felder konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const setVal = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));
  const toggleMulti = (key, opt) => setValues((prev) => {
    const cur = Array.isArray(prev[key]) ? prev[key] : [];
    return { ...prev, [key]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customers/${customerId}/custom-fields`, { values });
      setValues(res.data?.values || {});
      toast.success('Eigene Felder gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (f) => {
    const v = values[f.key];
    switch (f.type) {
      case 'select':
        return (
          <Select value={v || '__none__'} onValueChange={(val) => setVal(f.key, val === '__none__' ? '' : val)}>
            <SelectTrigger data-testid={`custom-field-input-${f.key}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">– keine Angabe –</SelectItem>
              {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-2" data-testid={`custom-field-input-${f.key}`}>
            {(f.options || []).map((o) => {
              const active = Array.isArray(v) && v.includes(o);
              return (
                <button key={o} type="button" onClick={() => toggleMulti(f.key, o)}
                  className={`px-3 py-1 rounded-full border text-sm transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
                  data-testid={`custom-field-${f.key}-opt-${o}`}>
                  {o}
                </button>
              );
            })}
          </div>
        );
      case 'textarea':
        return <textarea className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={v || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`custom-field-input-${f.key}`} />;
      case 'number':
        return <Input type="number" value={v ?? ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`custom-field-input-${f.key}`} />;
      case 'date':
        return <Input type="date" value={v || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`custom-field-input-${f.key}`} />;
      case 'boolean':
        return <Switch checked={!!v} onCheckedChange={(c) => setVal(f.key, c)} data-testid={`custom-field-input-${f.key}`} />;
      default:
        return <Input value={v || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`custom-field-input-${f.key}`} />;
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6" data-testid="custom-fields-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>;
  }

  const obBlock = obResponses.length > 0 ? (
    <div className="rounded-lg border bg-muted/30 p-3" data-testid="onboarding-responses">
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Aus dem Onboarding</p>
      <div className="grid grid-cols-2 gap-3">
        {obResponses.map((r) => (
          <div key={r.key} data-testid={`onboarding-response-${r.key}`}>
            <p className="text-xs text-muted-foreground">{r.label}</p>
            <p className="text-sm font-medium">{r.value}</p>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  if (!fields.length) {
    return (
      <div className="space-y-5" data-testid="custom-fields-tab">
        {obBlock}
        <div className="p-8 text-center text-muted-foreground" data-testid="custom-fields-empty">
          <ListChecks className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="font-medium">Keine eigenen Felder aktiv.</p>
          <p className="text-sm">Lege welche an unter Einstellungen → Eigene Kundenfelder.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="custom-fields-tab">
      {obBlock}
      {fields.map((f) => (
        <div key={f.id} className="space-y-1.5" data-testid={`custom-field-${f.key}`}>
          <Label className="text-sm font-medium flex items-center gap-2">
            {f.label}
            {f.required_booking && <span className="text-red-500">*</span>}
            {f.unit ? <span className="text-xs text-muted-foreground">({f.unit})</span> : null}
          </Label>
          {renderInput(f)}
        </div>
      ))}
      <div className="pt-2">
        <Button onClick={save} disabled={saving} data-testid="custom-fields-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </div>
    </div>
  );
};

export default CustomerCustomFieldsTab;
